import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlateMindLogoWithText } from "@/components/PlateMindLogo";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Users, Calendar, Target } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const JoinProgram = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", challenge_alias: "" });

  useEffect(() => {
    const fetchProgram = async () => {
      try {
        const res = await fetch(`${API}/corporate/join/${code}`);
        if (res.ok) {
          setProgram(await res.json());
        } else {
          const err = await res.json();
          setError(err.detail || "Invalid invite link");
        }
      } catch { setError("Unable to load program details"); }
      finally { setLoading(false); }
    };
    fetchProgram();
  }, [code]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/corporate/join/${code}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
        // Auto-login
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        setTimeout(() => { window.location.href = "/dashboard"; }, 2000);
      } else {
        toast.error(data.detail || "Failed to sign up");
      }
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full rounded-3xl text-center p-8">
          <div className="mb-4">
            <PlateMindLogoWithText />
          </div>
          <h2 className="text-xl font-bold text-secondary mb-2">Link Not Available</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate("/login")} className="bg-primary hover:bg-primary/90 rounded-xl">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full rounded-3xl text-center p-8 space-y-4">
          <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
          <h2 className="text-2xl font-bold text-secondary">You're In!</h2>
          <p className="text-muted-foreground">
            Welcome to <span className="font-semibold text-primary">{program.program_name}</span>.
            Taking you to your dashboard...
          </p>
          <Loader2 className="w-5 h-5 animate-spin text-primary mx-auto" />
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-orange-50/30 flex items-center justify-center p-4">
      <Card className="max-w-md w-full rounded-3xl overflow-hidden" data-testid="join-program-card">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-orange-400 p-6 text-white text-center space-y-2">
          <div className="flex justify-center mb-2">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Target className="w-7 h-7" />
            </div>
          </div>
          <p className="text-sm text-white/80">{program.company_name} presents</p>
          <h1 className="text-2xl font-bold">{program.program_name}</h1>
          {program.description && <p className="text-sm text-white/90">{program.description}</p>}
        </div>

        {/* Program Info */}
        <div className="flex border-b border-orange-100">
          <div className="flex-1 p-3 text-center border-r border-orange-100">
            <Calendar className="w-4 h-4 text-primary mx-auto mb-1" />
            <p className="text-[10px] text-muted-foreground">Starts</p>
            <p className="text-xs font-semibold text-secondary">
              {new Date(program.start_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </p>
          </div>
          <div className="flex-1 p-3 text-center border-r border-orange-100">
            <Calendar className="w-4 h-4 text-primary mx-auto mb-1" />
            <p className="text-[10px] text-muted-foreground">Ends</p>
            <p className="text-xs font-semibold text-secondary">
              {new Date(program.end_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </p>
          </div>
          <div className="flex-1 p-3 text-center">
            <Target className="w-4 h-4 text-primary mx-auto mb-1" />
            <p className="text-[10px] text-muted-foreground">Daily {program.challenge_target_label || "Sugar"}</p>
            <p className="text-xs font-semibold text-secondary">{program.challenge_target_value || program.sugar_target_grams || 25}{program.challenge_target_unit || "g"} max</p>
          </div>
        </div>

        {/* Signup Form */}
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Full Name *</Label>
              <Input placeholder="e.g. John Doe" value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="rounded-xl" data-testid="join-name" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Work Email *</Label>
              <Input type="email" placeholder="you@company.com" value={form.email}
                onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                className="rounded-xl" data-testid="join-email" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Create Password *</Label>
              <Input type="password" placeholder="Min 6 characters" value={form.password}
                onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                className="rounded-xl" data-testid="join-password" required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Challenge Nickname</Label>
              <Input placeholder="What should others call you?" value={form.challenge_alias}
                onChange={(e) => setForm(prev => ({ ...prev, challenge_alias: e.target.value }))}
                className="rounded-xl" data-testid="join-alias" />
              <p className="text-[10px] text-muted-foreground">This name appears on the leaderboard instead of your real name</p>
            </div>

            <Button type="submit" disabled={submitting}
              className="w-full bg-primary hover:bg-primary/90 rounded-xl h-11 text-base font-semibold"
              data-testid="join-submit">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Users className="w-5 h-5 mr-2" />}
              Join the Challenge
            </Button>
          </form>

          <p className="text-[10px] text-center text-muted-foreground mt-4">
            Already have an account? <a href="/login" className="text-primary font-medium hover:underline">Log in</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinProgram;
